import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { normalizeBarcode, matchBicycle } from '../utils/normalizeBarcode';
import { 
  ClipboardList, Search, Check, X, Printer, RefreshCw,
  CheckCircle, Circle, Bike, ScanLine, ScanBarcode, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import BarcodeScannerModal from '../components/BarcodeScannerModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Inventory() {
  const [bicycles, setBicycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState({});  // {bicycle_id: true/false}
  const [searchTerm, setSearchTerm] = useState('');
  const [scanMode, setScanMode] = useState(false);
  const [isCameraScanOpen, setIsCameraScanOpen] = useState(false);
  const [modelFilter, setModelFilter] = useState('all');
  const searchInputRef = useRef(null);

  // Fetch bicycles
  const fetchBicycles = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('veloflux_token');
      const res = await axios.get(`${API}/bicycles?status=activa`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBicycles(res.data);
    } catch (error) {
      toast.error('Error al cargar bicicletas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBicycles();
  }, []);

  // Toggle check for a bicycle
  const toggleCheck = (bicycleId) => {
    setChecked(prev => ({
      ...prev,
      [bicycleId]: !prev[bicycleId]
    }));
  };

  // Handle scan/search
  const handleSearch = useCallback((value) => {
    setSearchTerm(value);
    
    if (scanMode && value.trim()) {
      // Normalize scanned input
      const { normalized, stripped } = normalizeBarcode(value);
      
      // Try exact match first (canonical, stripped, raw)
      let found = bicycles.find(b => {
        const num = (b.number || '').toLowerCase();
        const ic = (b.internal_code || '').toLowerCase();
        const numStripped = num.replace(/[^a-z0-9]/g, '');
        const icStripped = ic.replace(/[^a-z0-9]/g, '');
        const raw = value.trim().toLowerCase();
        return (
          num === normalized.toLowerCase() ||
          ic === normalized.toLowerCase() ||
          numStripped === stripped ||
          icStripped === stripped ||
          num === raw || ic === raw
        );
      });
      
      // Fallback: partial match by sequence number
      if (!found) {
        const { partial } = matchBicycle(value, bicycles);
        if (partial.length === 1) found = partial[0];
      }
      
      if (found) {
        setChecked(prev => ({ ...prev, [found.id]: true }));
        toast.success(`${found.number} marcada`);
        setSearchTerm('');
        setTimeout(() => searchInputRef.current?.focus(), 100);
      } else {
        toast.error(`No encontrada: "${normalized}"`);
      }
    }
  }, [bicycles, scanMode]);

  // Handle Enter key for scan mode
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && scanMode && searchTerm.trim()) {
      handleSearch(searchTerm);
    }
  };

  // Handle camera scan result
  const handleCameraScan = useCallback((code) => {
    if (!code) return;
    const { normalized } = normalizeBarcode(code);
    handleSearch(normalized);
  }, [handleSearch]);

  // Unique models for filter
  const uniqueModels = useMemo(() => 
    [...new Set(bicycles.map(b => b.model).filter(Boolean))].sort(),
    [bicycles]
  );

  // Filter bicycles based on search and model filter
  const filteredBicycles = bicycles.filter(b => {
    if (modelFilter !== 'all' && b.model !== modelFilter) return false;
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      b.number?.toLowerCase().includes(term) ||
      b.model?.toLowerCase().includes(term) ||
      b.size?.toLowerCase().includes(term) ||
      b.internal_code?.toLowerCase().includes(term) ||
      b.frame_number?.toLowerCase().includes(term)
    );
  });

  // Sort by model and then number
  const sortedBicycles = [...filteredBicycles].sort((a, b) => {
    if (a.model !== b.model) return a.model?.localeCompare(b.model) || 0;
    return (a.number || '').localeCompare(b.number || '', undefined, { numeric: true });
  });

  // Stats
  const totalCount = bicycles.length;
  const checkedCount = Object.values(checked).filter(Boolean).length;
  const uncheckedCount = totalCount - checkedCount;

  // Print inventory list
  const printInventory = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('No se pudo abrir la ventana de impresión');
      return;
    }

    const date = new Date().toLocaleDateString('es-ES', {
      year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Inventario de Bicicletas</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { text-align: center; margin-bottom: 5px; }
          .date { text-align: center; color: #666; margin-bottom: 20px; }
          .stats { display: flex; justify-content: center; gap: 30px; margin-bottom: 20px; }
          .stat { text-align: center; }
          .stat-value { font-size: 24px; font-weight: bold; }
          .stat-label { font-size: 12px; color: #666; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f5f5f5; }
          .check-col { width: 40px; text-align: center; }
          .checked { background-color: #dcfce7; }
          .unchecked { background-color: #fef2f2; }
          @media print {
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <h1>Inventario de Bicicletas</h1>
        <p class="date">${date}</p>
        <div class="stats">
          <div class="stat">
            <div class="stat-value">${totalCount}</div>
            <div class="stat-label">Total</div>
          </div>
          <div class="stat">
            <div class="stat-value" style="color: green">${checkedCount}</div>
            <div class="stat-label">Encontradas</div>
          </div>
          <div class="stat">
            <div class="stat-value" style="color: red">${uncheckedCount}</div>
            <div class="stat-label">Pendientes</div>
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th>Modelo</th>
              <th>Talla</th>
              <th>Código</th>
              <th class="check-col">✓</th>
            </tr>
          </thead>
          <tbody>
            ${sortedBicycles.map(b => `
              <tr class="${checked[b.id] ? 'checked' : 'unchecked'}">
                <td>${b.model || '-'}</td>
                <td>${b.size || '-'}</td>
                <td>${b.number || b.internal_code || '-'}</td>
                <td class="check-col">${checked[b.id] ? '✓' : '☐'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <script>window.print();</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Reset all checks
  const resetChecks = () => {
    setChecked({});
    toast.success('Inventario reiniciado');
  };

  // Mark all as checked
  const markAllChecked = () => {
    const allChecked = {};
    bicycles.forEach(b => { allChecked[b.id] = true; });
    setChecked(allChecked);
    toast.success('Todas marcadas como encontradas');
  };

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ClipboardList className="w-7 h-7 text-orange-500" />
            Inventario de Bicicletas
          </h1>
          <p className="text-slate-500 text-sm">Marca las bicicletas encontradas</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={scanMode ? "default" : "outline"}
            size="sm"
            onClick={() => {
              setScanMode(!scanMode);
              if (!scanMode) {
                setTimeout(() => searchInputRef.current?.focus(), 100);
              }
            }}
            data-testid="scan-mode-btn"
            className={scanMode ? "bg-green-600 hover:bg-green-700" : ""}
          >
            <ScanLine className="w-4 h-4 mr-2" />
            {scanMode ? 'Modo Escaneo ON' : 'Modo Escaneo'}
          </Button>
          <Button variant="outline" size="sm" onClick={printInventory} data-testid="print-btn">
            <Printer className="w-4 h-4 mr-2" />
            Imprimir
          </Button>
          <Button variant="outline" size="sm" onClick={resetChecks}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Reiniciar
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold text-slate-900">{totalCount}</div>
            <div className="text-sm text-slate-500">Total</div>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold text-green-600">{checkedCount}</div>
            <div className="text-sm text-green-700">Encontradas</div>
          </CardContent>
        </Card>
        <Card className={uncheckedCount > 0 ? "border-red-200 bg-red-50" : ""}>
          <CardContent className="pt-4 text-center">
            <div className={`text-3xl font-bold ${uncheckedCount > 0 ? 'text-red-600' : 'text-slate-400'}`}>
              {uncheckedCount}
            </div>
            <div className={`text-sm ${uncheckedCount > 0 ? 'text-red-700' : 'text-slate-500'}`}>
              Pendientes
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            ref={searchInputRef}
            type="text"
            placeholder={scanMode ? "Escanea o escribe el código..." : "Buscar por código, modelo, talla..."}
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            className={`pl-10 ${scanMode ? 'ring-2 ring-green-500' : ''}`}
            data-testid="inventory-search"
            autoFocus={scanMode}
          />
        </div>
        <Select value={modelFilter} onValueChange={setModelFilter}>
          <SelectTrigger className="w-48" data-testid="inventory-model-filter">
            <Filter className="w-4 h-4 mr-1 text-slate-400" />
            <SelectValue placeholder="Modelo..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los modelos</SelectItem>
            {uniqueModels.map(m => (
              <SelectItem key={m} value={m}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(modelFilter !== 'all' || searchTerm) && (
          <Button variant="ghost" size="sm" onClick={() => { setModelFilter('all'); setSearchTerm(''); }}
            className="text-slate-500 hover:text-slate-700" data-testid="clear-inventory-filters">
            <X className="w-4 h-4 mr-1" /> Limpiar
          </Button>
        )}
        <Button variant="outline" size="icon"
          className="flex-shrink-0 border-orange-300 text-orange-600 hover:bg-orange-50"
          onClick={() => setIsCameraScanOpen(true)}
          title="Escanear con cámara"
          data-testid="camera-scan-btn">
          <ScanBarcode size={20} />
        </Button>
        <Button variant="outline" onClick={markAllChecked}>
          <CheckCircle className="w-4 h-4 mr-2" />
          Marcar todas
        </Button>
      </div>

      {/* Scan mode indicator */}
      {scanMode && (
        <div className="bg-green-100 border border-green-300 rounded-lg p-3 flex items-center gap-2">
          <ScanLine className="w-5 h-5 text-green-600" />
          <span className="text-green-800 font-medium">
            Modo escaneo activo - Escanea el código de barras o escribe el código y pulsa Enter
          </span>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner w-8 h-8" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">Modelo</TableHead>
                  <TableHead className="w-20">Talla</TableHead>
                  <TableHead>Código</TableHead>
                  <TableHead className="w-24 text-center">Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedBicycles.map(bicycle => (
                  <TableRow 
                    key={bicycle.id}
                    className={`cursor-pointer transition-colors ${
                      checked[bicycle.id] 
                        ? 'bg-green-50 hover:bg-green-100' 
                        : 'hover:bg-slate-50'
                    }`}
                    onClick={() => toggleCheck(bicycle.id)}
                    data-testid={`inventory-row-${bicycle.id}`}
                  >
                    <TableCell className="font-medium">{bicycle.model}</TableCell>
                    <TableCell>{bicycle.size}</TableCell>
                    <TableCell>
                      <code className="bg-slate-100 px-2 py-1 rounded text-sm">
                        {bicycle.number || bicycle.internal_code || '-'}
                      </code>
                    </TableCell>
                    <TableCell className="text-center">
                      {checked[bicycle.id] ? (
                        <Badge className="bg-green-500">
                          <Check className="w-3 h-3 mr-1" />
                          OK
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-400">
                          <Circle className="w-3 h-3 mr-1" />
                          Pendiente
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {sortedBicycles.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                      <Bike className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                      No se encontraron bicicletas
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Camera Barcode Scanner */}
      <BarcodeScannerModal
        open={isCameraScanOpen}
        onClose={() => setIsCameraScanOpen(false)}
        onScan={handleCameraScan}
      />
    </div>
  );
}
